import moment from "moment-timezone";
import { STATUS_VARIANT_MAP } from "@/shared/utils/constants";

/** API may send snake_case or camelCase. */
export function getCampaignStartSchedule(campaign) {
  if (!campaign || typeof campaign !== "object") return null;
  return campaign.start_schedule ?? campaign.startSchedule ?? null;
}

function getScheduleMode(schedule) {
  if (!schedule) return null;
  return schedule.startMode ?? schedule.start_mode ?? null;
}

function isScheduleStartInFuture(schedule) {
  if (getScheduleMode(schedule) !== "schedule_later") {
    return false;
  }
  if (schedule.scheduledForUtc) {
    const t = new Date(schedule.scheduledForUtc).getTime();
    if (!Number.isNaN(t)) {
      return t > Date.now();
    }
  }
  const m = resolveScheduleMoment(schedule);
  return Boolean(m && m.isValid() && m.isAfter(moment()));
}

/**
 * Resolve when the campaign is meant to start (for schedule_later).
 * Prefers UTC from API, then wall time in campaign timezone.
 */
function resolveScheduleMoment(schedule) {
  if (!schedule) return null;
  if (schedule.scheduledForUtc) {
    const m = moment.utc(schedule.scheduledForUtc);
    return m.isValid() ? m : null;
  }
  const tz = schedule.timezone || schedule.normalizedTimezone;
  if (schedule.scheduledAt && tz) {
    const m = moment.tz(schedule.scheduledAt, tz);
    return m.isValid() ? m : null;
  }
  if (schedule.scheduledAt) {
    const m = moment(schedule.scheduledAt);
    return m.isValid() ? m : null;
  }
  return null;
}

/**
 * Human-readable line for the scheduled start (timezone-aware).
 */
export function formatCampaignScheduleSubtitle(schedule) {
  if (!schedule) return "";
  const tz =
    schedule.timezone ||
    schedule.normalizedTimezone ||
    Intl.DateTimeFormat().resolvedOptions().timeZone;
  const m = resolveScheduleMoment(schedule);
  if (!m || !m.isValid()) {
    if (schedule.scheduledAt) return String(schedule.scheduledAt);
    return "Scheduled";
  }
  const local = m.clone().tz(tz);
  return `${local.format("ddd, MMM D, YYYY · h:mm A")} (${tz})`;
}

/**
 * Whether to show the "Scheduled" run control (badge + subtitle + pause).
 * - Explicit API status `scheduled`
 * - Or `active` with `schedule_later` and start time still in the future
 */
export function getCampaignScheduledRunState(campaign) {
  if (!campaign) {
    return { isScheduled: false, subtitle: "" };
  }
  const st = String(campaign.status || "").toLowerCase();
  const schedule = getCampaignStartSchedule(campaign);

  if (st === "paused") {
    return { isScheduled: false, subtitle: "" };
  }

  if (st === "draft" && getScheduleMode(schedule) === "schedule_later") {
    if (isScheduleStartInFuture(schedule)) {
      return {
        isScheduled: true,
        subtitle: formatCampaignScheduleSubtitle(schedule),
      };
    }
    return { isScheduled: true, subtitle: "Starting shortly…" };
  }

  if (st === "scheduled") {
    if (
      getScheduleMode(schedule) === "schedule_later" &&
      !isScheduleStartInFuture(schedule)
    ) {
      return { isScheduled: true, subtitle: "Starting shortly…" };
    }
    const subtitle = formatCampaignScheduleSubtitle(schedule);
    return { isScheduled: true, subtitle: subtitle || "Scheduled" };
  }
  if (st === "active" && getScheduleMode(schedule) === "schedule_later") {
    if (isScheduleStartInFuture(schedule)) {
      return {
        isScheduled: true,
        subtitle: formatCampaignScheduleSubtitle(schedule),
      };
    }
    if (!schedule.scheduledForUtc && schedule.scheduledAt) {
      const subtitle = formatCampaignScheduleSubtitle(schedule);
      return { isScheduled: true, subtitle: subtitle || "Scheduled" };
    }
  }
  return { isScheduled: false, subtitle: "" };
}

/**
 * Status column label + chip variant for campaign list (API status stays "active").
 */
export function getCampaignStatusMetaForTable(campaign) {
  if (!campaign) {
    return { label: "", variant: "neutral" };
  }
  if (getCampaignScheduledRunState(campaign).isScheduled) {
    return { label: "Scheduled", variant: STATUS_VARIANT_MAP.Scheduled || "neutral" };
  }
  const raw = campaign.status ?? "";
  const label =
    raw === ""
      ? ""
      : raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
  const variant =
    STATUS_VARIANT_MAP[
      raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase()
    ] || "neutral";
  return { label, variant };
}
